// frontend/src/features/stock/hooks/useStockPage.js

import { useEffect, useMemo, useRef, useState } from "react";

import {
  getStockItems,
  createStockItem,
  updateStockItem,
  updateStockItemStatus,
  deleteStockItem,
} from "../services/stockService";

import {
  toNumber,
  groupStockItemsByMedication,
  hasUsableStockItem,
  STOCK_STATUS,
} from "../utils/stockFormatters";

export const useStockPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState({
    text: "",
    type: "",
  });

  const [isFormOpen, setIsFormOpen] = useState(false);

  const [editingId, setEditingId] = useState(null);

  // ✅ NOVO
  const [mode, setMode] = useState("new-medication");

  const messageTimerRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    brand: "",

    medicationName: "",
    packagingLabel: "",

    medicationType: "INJETAVEL",

    // INJETÁVEL
    mgBase: "",
    mlBase: "",
    totalVolumeMl: "",

    // COMPRIMIDO / CÁPSULA
    mgPerUnit: "",
    totalUnits: "",

    startDate: new Date().toISOString().split("T")[0],

    status: STOCK_STATUS.DISPONIVEL,
  });

  const clearMessageSoon = () => {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
    }

    messageTimerRef.current = setTimeout(() => {
      setMessage({
        text: "",
        type: "",
      });

      messageTimerRef.current = null;
    }, 3500);
  };

  const showMessage = (
    text,
    type = "success"
  ) => {
    setMessage({
      text,
      type,
    });

    clearMessageSoon();
  };

  const fetchItems = async () => {
    setLoading(true);

    try {
      const data = await getStockItems();

      setItems(data);

      setIsFormOpen(data.length === 0);
    } catch (e) {
      showMessage(
        e?.response?.data?.message ||
          "Erro ao carregar estoque.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();

    return () => {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
      }
    };
  }, []);

  const mgBaseN = useMemo(
    () => toNumber(form.mgBase),
    [form.mgBase]
  );

  const mlBaseN = useMemo(
    () => toNumber(form.mlBase),
    [form.mlBase]
  );

  const totalVolumeN = useMemo(
    () => toNumber(form.totalVolumeMl),
    [form.totalVolumeMl]
  );

  const mgPerUnitN = useMemo(
    () => toNumber(form.mgPerUnit),
    [form.mgPerUnit]
  );

  const totalUnitsN = useMemo(
    () => toNumber(form.totalUnits),
    [form.totalUnits]
  );

  const groupedItems = useMemo(
    () => groupStockItemsByMedication(items),
    [items]
  );

  const hasUsableItems = useMemo(
    () => hasUsableStockItem(items),
    [items]
  );

  const resetForm = () => {
    setEditingId(null);

    // ✅ NOVO
    setMode("new-medication");

    setForm({
      name: "",
      brand: "",

      medicationName: "",
      packagingLabel: "",

      medicationType: "INJETAVEL",

      mgBase: "",
      mlBase: "",
      totalVolumeMl: "",

      mgPerUnit: "",
      totalUnits: "",

      startDate: new Date()
        .toISOString()
        .split("T")[0],

      status: STOCK_STATUS.DISPONIVEL,
    });
  };

  const openNewForm = () => {
    resetForm();

    // ✅ NOVO
    setMode("new-medication");

    setIsFormOpen(true);
  };

  // ✅ NOVO
  const openPackageForm = (
    medication
  ) => {
    if (!medication) return;

    setEditingId(null);

    setMode("new-package");

    setForm({
      name: medication.name || "",
      brand: "",

      medicationName:
        medication.name || "",

      packagingLabel: "",

      medicationType:
        medication.medicationType ||
        "INJETAVEL",

      mgBase: "",
      mlBase: "",
      totalVolumeMl: "",

      mgPerUnit:
        medication.mgPerUnit || "",

      totalUnits: "",

      startDate: new Date()
        .toISOString()
        .split("T")[0],

      status: STOCK_STATUS.DISPONIVEL,
    });

    setIsFormOpen(true);
  };

  const startEdit = (item) => {
    const id = item?.id ?? item?._id;

    if (!id) return;

    const st = item?.status;

    const canEdit =
      st === STOCK_STATUS.PLANEJADO ||
      st === STOCK_STATUS.DISPONIVEL;

    if (!canEdit) {
      showMessage(
        "Só é possível editar itens planejados ou disponíveis.",
        "error"
      );

      return;
    }

    setEditingId(id);

    setMode("new-package");

    setForm({
      name: item?.name || "",
      brand: item?.brand || "",

      medicationName:
        item?.medicationName ||
        item?.name ||
        "",

      packagingLabel:
        item?.packagingLabel ||
        item?.brand ||
        "",

      medicationType:
        item?.medicationType ||
        "INJETAVEL",

      startDate:
        item?.startDate ||
        new Date()
          .toISOString()
          .split("T")[0],

      mgBase: String(item?.mgBase || ""),
      mlBase: String(item?.mlBase || ""),
      totalVolumeMl: String(
        item?.totalVolumeMl || ""
      ),

      mgPerUnit: String(
        item?.mgPerUnit || ""
      ),

      totalUnits: String(
        item?.totalUnits || ""
      ),

      status:
        st === STOCK_STATUS.PLANEJADO
          ? STOCK_STATUS.PLANEJADO
          : STOCK_STATUS.DISPONIVEL,
    });

    setIsFormOpen(true);
  };

  // ✅ CORRIGIDO
  const handleSubmit = async (
    payloadFromForm
  ) => {
    setSubmitting(true);

    setMessage({
      text: "",
      type: "",
    });

    try {
      if (editingId) {
        await updateStockItem(
          editingId,
          payloadFromForm
        );

        showMessage(
          "Atualizado com sucesso."
        );
      } else {
        await createStockItem(
          payloadFromForm
        );

        if (mode === "new-package") {
          showMessage(
            "Nova embalagem adicionada."
          );
        } else {
          showMessage(
            "Medicação cadastrada com sucesso."
          );
        }
      }

      await fetchItems();

      setIsFormOpen(false);

      resetForm();
    } catch (e) {
      showMessage(
        e?.response?.data?.message ||
          "Erro ao salvar.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (
    id,
    action
  ) => {
    try {
      await updateStockItemStatus(
        id,
        action
      );

      await fetchItems();

      if (action === "START") {
        showMessage(
          "Status atualizado com sucesso."
        );
      } else if (action === "STOP") {
        showMessage(
          "Uso pausado com sucesso."
        );
      } else if (action === "ARCHIVE") {
        showMessage(
          "Item arquivado com sucesso."
        );
      } else {
        showMessage(
          "Status atualizado com sucesso."
        );
      }
    } catch (e) {
      showMessage(
        e?.response?.data?.message ||
          "Erro ao atualizar status.",
        "error"
      );
    }
  };

  const deleteItem = async (id) => {
    try {
      const response =
        await deleteStockItem(id);

      showMessage(
        response?.message ||
          "Item excluído com sucesso."
      );

      await fetchItems();
    } catch (e) {
      showMessage(
        e?.response?.data?.message ||
          "Erro ao excluir item.",
        "error"
      );
    }
  };

  return {
    items,
    groupedItems,

    loading,
    submitting,

    message,

    form,
    mode,

    isFormOpen,
    editingId,

    hasUsableItems,

    setForm,
    setIsFormOpen,
    setEditingId,

    openNewForm,

    // ✅ NOVO
    openPackageForm,

    resetForm,
    startEdit,

    handleSubmit,

    updateStatus,
    deleteItem,

    showMessage,
  };
};